import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Mail } from 'lucide-react';
import { supabase } from '../lib/supabaseClient';
import { validateEmail } from '../utils/memberUtils';
import { AUTH_LIGHT_SCREEN_BG, useBodyBackgroundOnMount } from '../hooks/useBodyBackgroundOnMount';

export default function ForgotPassword() {
  useBodyBackgroundOnMount(AUTH_LIGHT_SCREEN_BG);
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    
    // バリデーション
    const emailValidation = validateEmail(email);
    if (!emailValidation.valid) {
      setFormError(emailValidation.error ?? '有効なメールアドレスを入力してください');
      return;
    }

    setLoading(true);
    try {
      const normalized = email.trim().toLowerCase();

      // Demo account policy: do not allow password resets for the shared demo user.
      if (normalized === 'demo@handra.jp') {
        setFormError('デモユーザーはパスワード再設定できません');
        return;
      }

      const redirectTo = new URL('/reset-password', window.location.origin).toString();
      const { error } = await supabase.auth.resetPasswordForEmail(normalized, { redirectTo });
      if (error) throw error;
      setSent(true);
    } catch (error: any) {
      console.error('Password reset request error:', error);
      setFormError(error.message || 'パスワードリセットリクエストに失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const handleBackToLogin = () => {
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 to-blue-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl font-bold text-teal-600">
            Handra
          </CardTitle>
          <CardDescription>
            パスワードリセット
          </CardDescription>
        </CardHeader>
        <CardContent>
          {sent ? (
            <div className="space-y-4">
              <p className="text-center font-semibold text-gray-900">
                パスワードリセット用のメールを送信しました。
              </p>
              <p className="text-left text-sm leading-relaxed text-gray-900">
                メールに記載されたリンクから、新しいパスワードを設定してください。メールが届かない場合は、迷惑メールフォルダと入力したメールアドレスを確認し、少し時間をおいてからもう一度お試しください。この画面を閉じても問題ありません。
              </p>
              <Button type="button" variant="cta" className="w-full" onClick={handleBackToLogin}>
                ログイン画面に戻る
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">メールアドレス</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input
                    id="email"
                    type="text"
                    inputMode="email"
                    autoComplete="email"
                    placeholder="example@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 placeholder:text-gray-300"
                    required
                    disabled={loading}
                  />
                </div>
                <p className="text-xs text-gray-500">
                  登録されているメールアドレスを入力してください
                </p>
              </div>
              
              {formError && (
                <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">
                  {formError}
                </p>
              )}

              <div className="pt-6">
                <Button type="submit" variant="cta" className="w-full" disabled={loading}>
                  {loading ? '送信中...' : 'リセットメールを送信'}
                </Button>
              </div>
              
              <div className="text-center text-sm">
                <Link to="/login" className="text-teal-600 hover:underline">
                  戻る
                </Link>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}




